import React from "react";
import { MatHead } from "../sections/kiTechnologie/berater/mat-head";
import { MAT_PALETTE as C } from "../../styles/themes/mat-palette";

export type MatFigureProps = {
    /** Cyan stroke/visor tuned for dark backgrounds (matches MatAvatar's onDark). */
    onDark?: boolean;
    className?: string;
    /** Wird nach dem Klick-Sprung aufgerufen (z.B. Navigation). Gesetzt = Mat
     *  ist interaktiv; fehlt = rein dekorativer Klick-Sprung ohne Folgeaktion. */
    onActivate?: () => void;
};

/**
 * «Mat» als freundliche Ei-Figur mit winkendem Ärmchen für den Header-Teaser.
 * Nutzt den kanonischen Kopf (MatHead) und ergänzt Ei-Körper + Arme. Rein
 * dekorativ (aria-hidden); der sprechende Text steht in der Sprechblase des
 * MatTeaserBadge. Animationen (Schweben, Blinzeln, Antenne, Winken) via
 * index.css und werden bei prefers-reduced-motion eingefroren.
 *
 * Viewport 130x165: Kopf im oberen 120er-Raster (wie MatHead), Ei darunter.
 */
export const MatFigure: React.FC<MatFigureProps> = ({ onDark = false, className, onActivate }) => {
    const stroke = onDark ? C.mint : C.purpleMid;
    const visor = onDark ? C.visor : C.purple;
    const shadowFill = onDark ? C.shadowOnDark : C.purpleMid;
    const shadowOpacity = onDark ? 0.28 : 0.12;

    // Eindeutige IDs pro Instanz, damit mehrere MatFigure auf einer Seite sich
    // nicht die Gradient-/Masken-Definitionen streitig machen.
    const glowId = React.useId();
    const shineMaskId = React.useId();
    const shineGradId = React.useId();

    const [isJumping, setIsJumping] = React.useState(false);

    const handleClick = () => {
        if (isJumping) {
            return; // Doppelklick-Schutz
        }
        setIsJumping(true);
        // Fallback: falls die Animation nicht feuert (prefers-reduced-motion),
        // nach der Sprung-Dauer trotzdem abschliessen + navigieren. Muss ≥ die
        // Keyframe-Dauer (0.7s, siehe mat-jump in index.css) sein, sonst würde
        // der Timeout auf der Homepage die Navigation auslösen, bevor der Sprung
        // fertig ist. Bei animierter Wiedergabe gewinnt ohnehin onAnimationEnd.
        window.setTimeout(() => {
            setIsJumping(was => {
                if (was) {
                    onActivate?.();
                }
                return false;
            });
        }, 720);
    };
    const handleJumpEnd = (event: React.AnimationEvent<SVGGElement>) => {
        // Nur das Ende des Sprungs selbst darf den Zustand beenden. onAnimationEnd
        // feuert für JEDES animationend, das aus der Gruppe hochblubbert – und die
        // enthält Dauer-Animationen (mat-shine, mat-wave, mat-blink). Ohne diesen
        // Namens-Filter würde ein Kind-Event den Sprung sofort abbrechen (Flackern
        // statt Sprung). Der Bubble-Ursprung wird ignoriert; nur der Name zählt.
        if (event.animationName !== "mat-jump") {
            return;
        }
        setIsJumping(was => {
            if (was) {
                onActivate?.();
            }
            return false;
        });
    };

    return (
        <svg
            data-testid="mat-figure"
            aria-hidden="true"
            className={[className, "mat-interactive"].filter(Boolean).join(" ")}
            viewBox="0 0 130 165"
            fill="none"
            style={{ overflow: "visible" }}
            onClick={handleClick}
        >
            <defs>
                <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={C.glow} stopOpacity="0.6" />
                    <stop offset="30%" stopColor={C.mint} stopOpacity="0.3" />
                    <stop offset="65%" stopColor={C.mint} stopOpacity="0.12" />
                    <stop offset="100%" stopColor={C.mint} stopOpacity="0" />
                </radialGradient>
                {/* Maske aus dem Wappen-PNG (weiss = sichtbar): beschränkt den
                    Shine-Streifen auf die Diamant-Silhouette, damit die
                    transparenten PNG-Ecken nicht mitleuchten. */}
                <mask id={shineMaskId} maskContentUnits="userSpaceOnUse">
                    <image
                        href="/images/superq-emblem.png"
                        x="36"
                        y="92"
                        width="48"
                        height="37"
                        preserveAspectRatio="xMidYMid meet"
                        style={{ filter: "brightness(0) invert(1)" }}
                    />
                </mask>
                {/* Schmaler Licht-Streifen: transparent → hell → transparent,
                    diagonal geneigt, damit der Sweep zur Diamant-Form passt. */}
                <linearGradient id={shineGradId} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={C.white} stopOpacity="0" />
                    <stop offset="50%" stopColor={C.white} stopOpacity="0.55" />
                    <stop offset="100%" stopColor={C.white} stopOpacity="0" />
                </linearGradient>
            </defs>
            {/* Weicher Cyan-Schein hinter Mat – hebt ihn vom dunklen Hero ab.
                Höher zentriert (auf Kopf/Körper) und grösser, damit die ganze
                Figur im Licht sitzt und der Schein sanft ausläuft. Ausserhalb
                der bob-Gruppe, damit der Schein ruhig bleibt. */}
            <ellipse className="mat-glow" cx="65" cy="72" rx="105" ry="110" fill={`url(#${glowId})`} />
            <g className="mat-figure-body" transform="translate(5,0)">
                {/* Der Klick-Sprung sitzt auf einer EIGENEN inneren Gruppe, nicht
                    auf .mat-figure-body. Grund: .mat-figure-body trägt bereits die
                    Dauer-Animation `mat-figure-bob`. Beide Sprung+Bob per
                    zweistelliger `animation`-Liste + positionsbasiertem
                    `animation-play-state: running, paused` auf demselben Element zu
                    fahren ist fragil – der Browser ordnet die Liste beim
                    Klassenwechsel nach Namen um, wodurch `paused` auf den Sprung
                    fallen kann und dieser NIE startet (Flackern statt Sprung). Eine
                    dedizierte Sprung-Ebene mit nur EINER Animation startet dagegen
                    zuverlässig; die Transforms (Bob aussen × Sprung innen)
                    komponieren sauber. */}
                {/* Boden-Schatten – bleibt AUSSERHALB der Sprung-Ebene, damit er
                    beim Sprung am Boden bleibt (ein Schatten springt nicht mit).
                    Beim Sprung schrumpft/verblasst er leicht (mat-jump-shadow),
                    weil Mat den Boden verlässt – verstärkt die Höhenwirkung. */}
                <ellipse
                    className={isJumping ? "mat-shadow mat-jump-shadow" : "mat-shadow"}
                    cx="60"
                    cy="156"
                    rx="26"
                    ry="4.5"
                    fill={shadowFill}
                    opacity={shadowOpacity}
                />
                <g
                    className={isJumping ? "mat-jump-layer mat-jump" : "mat-jump-layer"}
                    onAnimationEnd={isJumping ? handleJumpEnd : undefined}
                >
                    {/* Ganzflächiges Klick-Ziel: Mats gezeichnete Silhouette hat
                        transparente Lücken (um Kopf, Antenne, zwischen den Armen),
                        die per SVG-Default (visiblePainted) KEINE Klicks fangen –
                        ein Klick «auf Mat» dort ginge ins Leere. Dieses transparente
                        Rechteck (fill="transparent" wird gemalt, aber unsichtbar, und
                        fängt daher Pointer-Events) spannt die ganze Figur vom
                        Antennen-Ball (y≈16) bis zu den Füssen (y≈158) und über die
                        ausgestreckten Arme (x≈15…115) auf. Liegt in der Sprung-Ebene,
                        springt also mit. Kein Rand in den leeren Seitenbereich,
                        daher blockiert es nichts darunter. */}
                    <rect x="15" y="16" width="100" height="142" fill="transparent" />
                    {/* left arm (resting) */}
                    <path d="M30 98 q-9 6 -9 14" stroke={stroke} strokeWidth="6.5" fill="none" strokeLinecap="round" />
                    <circle cx="20" cy="116" r="5.5" fill={C.shell} stroke={stroke} strokeWidth="3" />
                    {/* egg body */}
                    <path
                        d="M60 66 C86 66 92 96 90 118 C88 140 76 152 60 152 C44 152 32 140 30 118 C28 96 34 66 60 66 Z"
                        fill={C.shell}
                        stroke={stroke}
                        strokeWidth="3.4"
                    />
                    <ellipse cx="60" cy="118" rx="15" ry="17" fill={C.mint} opacity="0.18" />
                    {/* «SuperQ»-Wappen als Brust-Emblem – das Superman-Schild mit
                        «Q» statt «S», identifiziert Mat als Qraftsman. Echtes Vektor-
                        Logo (aus der Marketing-.ai extrahiert, in Cyan umgefärbt) als
                        transparentes PNG-Asset (@6x, ~21KB) – bläht Mats JS-Bundle
                        nicht auf und rendert überall zuverlässig. Auf ~48px Breite
                        skaliert (Verhältnis 439:340) und zentriert auf die Brust
                        (x≈60, y≈92). Nur onDark-Cyan (Mat wird auf beiden Seiten
                        onDark eingesetzt). Rein dekorativ (aria-hidden). */}
                    <image
                        href="/images/superq-emblem.png"
                        x="36"
                        y="92"
                        width="48"
                        height="37"
                        aria-hidden="true"
                        preserveAspectRatio="xMidYMid meet"
                    />
                    {/* «Superhelden-Shine»: der Licht-Streifen (mat-shine wandert per
                        translateX) leuchtet nur auf der Wappen-Silhouette (Maske aus
                        dem PNG). Diagonal geneigt für den schrägen Sweep über den
                        Diamanten. Timing/Reduced-Motion via index.css. */}
                    <g mask={`url(#${shineMaskId})`}>
                        <g transform="rotate(18 60 110)">
                            {/* Ruhelage links ausserhalb der Maske (x=-30 … 12),
                                das Wappen beginnt bei x=36. Die mat-shine-Keyframes
                                fahren den Streifen +96px nach rechts durch das Wappen
                                und wieder zurück – so ist er in Ruhe unsichtbar
                                (wichtig für prefers-reduced-motion). */}
                            <rect
                                className="mat-shine"
                                x="-30"
                                y="86"
                                width="18"
                                height="49"
                                fill={`url(#${shineGradId})`}
                            />
                        </g>
                    </g>
                    {/* right arm (waving) – Schulter sitzt AUF der Körperkante (86,90)
                        und ist der Drehpunkt (mat-wave), damit der Arm beim Winken
                        mit dem Körper verbunden bleibt. Nach dem Körper gezeichnet,
                        also sichtbar oben angesetzt. */}
                    <g className="mat-wave">
                        <path
                            d="M86 90 q14 -2 20 -14"
                            stroke={stroke}
                            strokeWidth="6.5"
                            fill="none"
                            strokeLinecap="round"
                        />
                        <circle cx="108" cy="74" r="6" fill={C.shell} stroke={stroke} strokeWidth="3" />
                    </g>
                    {/* canonical head (shares geometry with MatAvatar) */}
                    <MatHead stroke={stroke} visor={visor} state="idle" />
                </g>
            </g>
        </svg>
    );
};

export default MatFigure;
